import type { ReactElement } from "react";
import { GLYPH_SIZES } from "./metrics.ts";

/**
 * The kit's small glyphs (ADR-061's amendment "the kit and the entry forms match the design"): original art under
 * `src/ui/art/marks/glyph/`, drawn by hand at the size in its name and never resampled, in white in the atlas and
 * drawn as a mask in the current text colour, as an icon is.
 */

/**
 * A glyph the kit draws.
 */
export type GlyphName = keyof typeof GLYPH_SIZES;

/**
 * The generated class that draws a glyph by `mask-position`, tinted with `currentColor`.
 *
 * @param name The glyph.
 * @returns `ot-ui-glyph-<name>_<size>`.
 * @example
 * glyphClass("alert");
 * // "ot-ui-glyph-alert_8"
 */
export function glyphClass(name: GlyphName): string {
  return `ot-ui-glyph-${name}_${GLYPH_SIZES[name]}`;
}

/**
 * A glyph in the current text colour, at its own size. Without a label it is decoration and hidden from assistive
 * technology; with one it is an image of that name.
 *
 * @param props The glyph, and a label when the glyph alone carries meaning.
 * @returns The glyph.
 */
export function Glyph({ name, label }: { readonly name: GlyphName; readonly label?: string }): ReactElement {
  if (label === undefined) {
    return <span className={`ot-icon ${glyphClass(name)}`} data-kit="glyph" aria-hidden="true" />;
  }
  return <span className={`ot-icon ${glyphClass(name)}`} data-kit="glyph" role="img" aria-label={label} />;
}
